import React, { useEffect, useState } from 'react';
import { View, Image, StyleSheet, ActivityIndicator, StyleProp, ImageStyle, ViewStyle } from 'react-native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { galleryAPI } from '../../services/api';
import { getCachedImage, setCachedImage, toBase64DataUri } from '../../utils/imageCache';
import { Logger } from '../../utils/logger';

interface Props {
  galleryId: string;
  style?: StyleProp<ImageStyle>;
  containerStyle?: StyleProp<ViewStyle>;
}

const CACHE_NAMESPACE = 'gallery';

// Standalone image component for the class gallery — same read-only endpoint the
// staff web frontend uses (GET /GalleryImage/{id}/download, src/frontend's
// GalleryImage.tsx), with a cache layer so the same image isn't re-downloaded and
// re-decoded from base64 every time it scrolls back into view or the screen reopens.
export function GalleryImage({ galleryId, style, containerStyle }: Props) {
  const [uri, setUri] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(false);

      const cached = await getCachedImage(CACHE_NAMESPACE, galleryId);
      if (cached) {
        if (!cancelled) {
          setUri(cached);
          setLoading(false);
        }
        return;
      }

      try {
        const { data } = await galleryAPI.downloadGalleryImage(galleryId);
        const base64 = data?.imageBase64;
        if (!base64) {
          if (!cancelled) setError(true);
          return;
        }
        const dataUri = toBase64DataUri(base64);
        await setCachedImage(CACHE_NAMESPACE, galleryId, dataUri);
        if (!cancelled) setUri(dataUri);
      } catch (err) {
        Logger.error('GalleryImage', `Failed to load image ${galleryId}`, err);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [galleryId]);

  if (loading) {
    return (
      <View style={[styles.placeholder, containerStyle]}>
        <ActivityIndicator size="small" color="#94A3B8" />
      </View>
    );
  }

  if (error || !uri) {
    return (
      <View style={[styles.placeholder, containerStyle]}>
        <FeatherIcon name="image" size={24} color="#94A3B8" />
      </View>
    );
  }

  return <Image source={{ uri }} style={style} onError={() => setError(true)} />;
}

const styles = StyleSheet.create({
  placeholder: {
    width: '100%',
    height: '100%',
    backgroundColor: '#F1F5F9',
    justifyContent: 'center',
    alignItems: 'center',
  },
});
